import { useEffect, useRef } from "react";
import {
  AdsManagerOnboardingPublicLandingPageButton,
  AdsManagerOnboardingPublicLandingPageButtonClicked,
  AdsManagerOnboardingPublicLandingPageView,
} from "@oai/analytics/gen/client-events/protobuf_analytics_events/v1/tapestry";

import { getUtmParameters, useClientTelemetry } from "@/lib/clientTelemetry";

import { CardGrid } from "./CardGrid";
import { Hero } from "./Hero";
import { LandingFooter } from "./LandingFooter";
import { LandingNav } from "./LandingNav";
import { Prefooter } from "./Prefooter";
import { ResourceCardGrid } from "./ResourceCardGrid";
import { SectionHeader } from "./SectionHeader";
import { TestimonialTabs } from "./TestimonialTabs";
import { TwoUpRows } from "./TwoUpRows";

import type { AdsLandingPage, AdsLandingPageExperimentParameters } from "./types";

export function LandingPage({
  landingPage,
  experimentParameters,
}: {
  landingPage: AdsLandingPage;
  experimentParameters: AdsLandingPageExperimentParameters;
}) {
  const { logEvent, logStructuredEvent } = useClientTelemetry();
  const hasLoggedPageViewRef = useRef(false);

  const logLandingPageButtonClick = (button: AdsManagerOnboardingPublicLandingPageButton) => {
    logStructuredEvent(AdsManagerOnboardingPublicLandingPageButtonClicked, {
      button,
      ...getUtmParameters(window.location.search),
    });
  };

  useEffect(() => {
    if (hasLoggedPageViewRef.current) {
      return;
    }
    hasLoggedPageViewRef.current = true;
    logEvent("bazaar.tapestry.onboarding_public_landing_page_view");
    logStructuredEvent(AdsManagerOnboardingPublicLandingPageView, {
      ...getUtmParameters(window.location.search),
      referrerUrl: document.referrer || undefined,
    });
  }, [logEvent, logStructuredEvent]);

  return (
    <div className="text-default bg-surface min-h-screen w-full overflow-x-hidden font-normal">
      <LandingNav
        ctaText={experimentParameters.headerCtaText}
        onCtaClick={logLandingPageButtonClick}
      />
      <main className="w-full">
        {landingPage.hero ?
          <Hero hero={landingPage.hero} experimentParameters={experimentParameters} />
        : null}

        {landingPage.sections.map((section, index) => (
          <section
            id={section.id}
            key={section.id ?? section.header?.headline ?? index}
            className="mx-auto flex w-full max-w-[1280px] flex-col px-0 pt-20 md:pt-[160px] lg:px-6"
          >
            {section.header ?
              <SectionHeader header={section.header} />
            : null}
            {section.cardGrid ?
              <CardGrid cardGrid={section.cardGrid} />
            : null}
            {section.twoUpRows ?
              <TwoUpRows rows={section.twoUpRows} />
            : null}
            {section.testimonialTabs ?
              <TestimonialTabs testimonialTabs={section.testimonialTabs} />
            : null}
            {section.resourceCardGrid ?
              <ResourceCardGrid cardGrid={section.resourceCardGrid} />
            : null}
          </section>
        ))}

        {landingPage.prefooter ?
          <Prefooter prefooter={landingPage.prefooter} />
        : null}
      </main>
      <LandingFooter />
    </div>
  );
}
